import React, { useEffect, useMemo, useState } from "react";
import {
  Badge,
  Button,
  Col,
  ProgressBar,
  Spinner,
  Table,
} from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { BsPencilSquare, BsTrash } from "react-icons/bs";
import { FaTasks } from "react-icons/fa";
import { IoDocumentTextOutline } from "react-icons/io5";
import { MdHistory } from "react-icons/md";
import { FcAcceptDatabase } from "react-icons/fc";
import { format } from "date-fns";
import ReactPaginate from "react-paginate";
import { toast } from "react-toastify";

import {
  deleteDeliverableById,
  fetchDeliverables,
} from "../../../../store/features/deliverable/deliverableSlice";
import { fetchUserById } from "../../../../store/features/user/userSlice";

import DeliverableHistoryModal from "./DeliverableHistoryModal";
import AcceptedDeliverableFormModal from "./AcceptedDeliverableFormModal";
import NoDataFound from "../../../../components/NoDataFound";

import FilterData from "../../../projectManagement/projects/components/FilterData";
import { STATUS_OPTIONS } from "../../../../utils/filterOptions";
import { filterBySearchAndStatus } from "../../../../utils/filterUtils";

const DeliverableTableData = ({
  setDeliverablesInfo,
  setShowDeliverableModal,
  setTabKey,
  wpInfo,
}) => {
  const dispatch = useDispatch();

  const { deliverables, loading, pagination } = useSelector(
    (state) => state.deliverables
  );
  const { role, _id } = useSelector((state) => state.auth);

  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [currentPage, setCurrentPage] = useState(0);

  const [expandedDescriptions, setExpandedDescriptions] = useState({});
  const [showDeliverableHistory, setShowDeliverableHistory] = useState(false);
  const [selectedDeliverable, setSelectedDeliverable] = useState({});
  const [showAccepted, setShowAccepted] = useState(false);
  const [deliverableInfoData, setDeliverableInfoData] = useState({});

  const limit = 10;

  useEffect(() => {
    dispatch(fetchDeliverables({ page: currentPage + 1, limit }));
  }, [dispatch, currentPage]);

  const toggleDescription = (id) =>
    setExpandedDescriptions((prev) => ({ ...prev, [id]: !prev[id] }));

  const wpFilteredDeliverables = useMemo(() => {
    if (wpInfo?._id) {
      return deliverables.filter((d) => d.workPackage?._id === wpInfo._id);
    }
    return deliverables;
  }, [deliverables, wpInfo]);

  const filteredDeliverables = useMemo(() => {
    return filterBySearchAndStatus({
      data: wpFilteredDeliverables,
      searchTerm,
      statusFilter,
      searchKeys: ["title", "description", "dlvNumber"],
    });
  }, [wpFilteredDeliverables, searchTerm, statusFilter]);

  const handleEdit = (data) => {
    setDeliverablesInfo(data);
    setShowDeliverableModal(true);
  };

  const handleDelete = async (id) => {
    try {
      await dispatch(deleteDeliverableById(id)).unwrap();
      await dispatch(fetchDeliverables({ page: currentPage + 1, limit }));
      await dispatch(fetchUserById(_id));
      toast.info("Deliverable deleted");
    } catch {
      toast.error("Error deleting Deliverable");
    }
  };

  const handleHistory = (deliverable) => {
    setSelectedDeliverable({
      deliverableId: deliverable._id,
      title: deliverable.title,
    });
    setShowDeliverableHistory(true);
  };

  const handleAcceptedModal = (data) => {
    setDeliverableInfoData(data);
    setShowAccepted(true);
  };

  return (
    <div>
      {loading ? (
        <Spinner animation="border" />
      ) : deliverables.length > 0 ? (
        <div className="table-wrapper mt-3">
          {/* FILTERS */}
          <FilterData
            searchValue={searchTerm}
            onSearchChange={setSearchTerm}
            searchPlaceholder="Search deliverables..."
            selectConfigs={[
              {
                label: "All Status",
                value: statusFilter,
                onChange: setStatusFilter,
                options: STATUS_OPTIONS,
              },
            ]}
          />

          <Col className="text-end me-2 mb-2">
            <Button
              size="sm"
              style={{ fontSize: 10 }}
              onClick={() => setTabKey("workPackages")}
            >
              Show All Deliverables
            </Button>
          </Col>

          <Table responsive>
            <thead>
              <tr>
                <th>Title</th>
                <th>Description</th>
                <th>Project</th>
                <th>Work Package</th>
                <th>Assigned To</th>
                <th>Number</th>
                <th>Cost</th>
                <th>Status</th>
                <th>Start</th>
                <th>End</th>
                <th>Attachments</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredDeliverables.map((d) => (
                <tr key={d._id}>
                  <td>
                    <span
                      className="text-primary cursor-pointer"
                      onClick={() => {
                        setDeliverablesInfo(d);
                        setTabKey("tasks");
                      }}
                    >
                      {d.title}
                    </span>
                    <p className="text-muted mb-0" style={{ fontSize: 12 }}>
                      {d.taskCount || 0} Tasks
                    </p>
                  </td>

                  <td>
                    {d.description?.length > 50 ? (
                      <>
                        {expandedDescriptions[d._id]
                          ? d.description
                          : `${d.description.slice(0, 50)}... `}
                        <span
                          onClick={() => toggleDescription(d._id)}
                          className="text-primary cursor-pointer"
                        >
                          {expandedDescriptions[d._id]
                            ? "Show less"
                            : "Show more"}
                        </span>
                      </>
                    ) : (
                      d.description
                    )}
                  </td>

                  <td>{d.project?.projectName}</td>
                  <td>{d.workPackage?.title}</td>
                  <td>{d.assignedUser?.name}</td>
                  <td>{d.dlvNumber}</td>
                  <td>{d.cost}</td>

                  <td>
                    <Badge bg="secondary">{d.status}</Badge>
                  </td>

                  <td>{format(new Date(d.startDate), "MM/dd/yyyy")}</td>
                  <td>{format(new Date(d.endDate), "MM/dd/yyyy")}</td>

                  <td>
                    {Array.isArray(d.deliverablesFiles) &&
                      d.deliverablesFiles.length > 0 && (
                        <div className="d-flex flex-wrap">
                          {d.deliverablesFiles.map((doc, i) => (
                            <Button key={i} variant="light" className="me-1">
                              <a
                                href={doc}
                                target="_blank"
                                rel="noreferrer"
                                download
                              >
                                <IoDocumentTextOutline size="1.2rem" />
                              </a>
                            </Button>
                          ))}
                        </div>
                      )}
                  </td>

                  <td className="actions">
                    {role === "staff" && (
                      <Button
                        size="sm"
                        title="Accepted"
                        onClick={() => handleAcceptedModal(d)}
                      >
                        <FcAcceptDatabase />
                      </Button>
                    )}

                    <Button
                      size="sm"
                      title="History"
                      onClick={() => handleHistory(d)}
                    >
                      <MdHistory />
                    </Button>

                    {role !== "staff" && role !== "auditor" && (
                      <>
                        <Button
                          size="sm"
                          title="Edit"
                          onClick={() => handleEdit(d)}
                        >
                          <BsPencilSquare />
                        </Button>
                        <Button
                          size="sm"
                          variant="danger"
                          title="Delete"
                          onClick={() => handleDelete(d._id)}
                        >
                          <BsTrash />
                        </Button>
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>

          {pagination?.totalPages > 1 && (
            <div className="d-flex justify-content-center mt-3">
              <ReactPaginate
                breakLabel="..."
                nextLabel="Next >"
                previousLabel="< Prev"
                onPageChange={(e) => setCurrentPage(e.selected)}
                pageCount={pagination.totalPages}
                forcePage={currentPage}
                containerClassName="pagination"
                activeClassName="active"
              />
            </div>
          )}
        </div>
      ) : (
        <NoDataFound
          icon={<FaTasks size={50} />}
          title="Deliverables not found"
          description="Try to add some deliverables."
        />
      )}

      {showDeliverableHistory && (
        <DeliverableHistoryModal
          showDeliverableHistory={showDeliverableHistory}
          setShowDeliverableHistory={setShowDeliverableHistory}
          selectedDeliverable={selectedDeliverable}
        />
      )}

      {showAccepted && (
        <AcceptedDeliverableFormModal
          showAccepted={showAccepted}
          setShowAccepted={setShowAccepted}
          deliverableInfoData={deliverableInfoData}
        />
      )}
    </div>
  );
};

export default DeliverableTableData;
